import { useCallback, useEffect, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useDirectSpeech } from '../../DirectSpeech';
import { useKeyboardShortcuts } from '../../KeyboardShortcuts';
import bundle from '../NovaComponents-List-gui-icu.properties';
const useTextLocalizer = createUseTextLocalizer(bundle);
export const useReadItems = ({
  tableRef,
  selectedRows,
  readItems: initialReadItemsState = {},
  getRowKey,
  readDelay = 2000
}) => {
  const localize = useTextLocalizer();
  const markedRead = localize('markedRead.aria');
  const markedUnread = localize('markedUnread.aria');
  const markAsRead = localize('markAsRead.label');
  const markAsUnread = localize('markAsUnread.label');
  const narration = useDirectSpeech();
  const notifyChange = useCallback(changes => {
    const readItemsCount = Object.keys(changes).map(key => changes[key]).filter(value => value).length;
    const unreadItemsCount = Object.keys(changes).map(key => changes[key]).filter(value => !value).length;
    if (readItemsCount > 0) {
      narration?.notify(markedRead);
    } else if (unreadItemsCount > 0) {
      narration?.notify(markedUnread);
    }
  }, [narration, markedRead, markedUnread]);
  const [readItems, _setReadItems] = useState(initialReadItemsState);
  const setReadItems = useCallback(updateFunction => {
    _setReadItems(prevState => {
      const newState = updateFunction(prevState);
      const changes = Object.keys(newState).reduce((acc, key) => {
        if (prevState[key] !== newState[key]) {
          if (prevState[key] === true && newState[key] === false) {
            acc[key] = false; // Mark as deleted
          } else {
            acc[key] = newState[key];
          }
        }
        return acc;
      }, {});

      // Handle keys that were in prevState but not in newState
      Object.keys(prevState).forEach(key => {
        if (!(key in newState) && prevState[key] === true) {
          changes[key] = false; // Mark as deleted
        }
      });

      // Remove "false" keys from the newState
      const filteredNewState = Object.keys(newState).reduce((acc, key) => {
        if (newState[key] !== false) {
          acc[key] = newState[key];
        }
        return acc;
      }, {});
      notifyChange(changes);
      return filteredNewState;
    });
  }, [notifyChange]);
  const readItemsSnapshotRef = useRef({});
  const {
    getActionsByKeyEvent
  } = useKeyboardShortcuts();
  const focusedCellRef = useRef({
    current: null
  });
  const readTimeController = useRef(null);
  const getItemKey = useCallback(rowIndex => getRowKey ? getRowKey(rowIndex) : String(rowIndex), [getRowKey]);
  const getContextMenuIndicatorItems = cell => {
    const targetCellInSelections = selectedRows && selectedRows.has(cell.rowKey);
    const selectedItems = targetCellInSelections ? Array.from(selectedRows) : [cell.rowKey];
    return selectedItems;
  };
  const readTime = () => {
    readTimeController.current?.abort();
    readTimeController.current = new AbortController();
    return new Promise(resolve => {
      const timeoutId = setTimeout(() => {
        if (tableRef?.current && document.activeElement === tableRef.current) {
          resolve();
        }
      }, readDelay);
      readTimeController.current?.signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        readTimeController.current = null;
      });
    });
  };
  const getContextMenuItems = cell => {
    const selectedItems = getContextMenuIndicatorItems(cell);
    const hasUnread = selectedItems.some(key => readItems[key]);
    const hasRead = selectedItems.some(key => !readItems[key]);
    const items = [...(hasRead ? [{
      text: markAsRead
    }] : []), ...(hasUnread ? [{
      text: markAsUnread
    }] : [])];
    return items;
  };
  const onFocusChange = focusedCell => {
    focusedCellRef.current = focusedCell;
    readTimeController.current?.abort();
    readTime().then(() => {
      setReadItems(prevState => ({
        ...prevState,
        [getItemKey(focusedCell.rowIndex)]: true
      }));
    });
  };
  const onContextMenuDismiss = () => {
    const hasChanges = Object.keys(readItems).some(key => readItemsSnapshotRef.current[key] !== readItems[key]);
    if (!hasChanges) {
      const rowIndex = focusedCellRef.current?.rowIndex;
      if (!isNullish(rowIndex)) {
        readTime().then(() => {
          setReadItems(prevState => ({
            ...prevState,
            [getItemKey(rowIndex)]: true
          }));
        });
      }
    }
  };
  const onContextMenuItemSelected = (itemText, cell) => {
    const selectedItems = getContextMenuIndicatorItems(cell);
    selectedItems.forEach(key => {
      setReadItems(prevState => {
        const newState = {
          ...prevState,
          [key]: itemText === markAsRead
        };
        if (itemText !== markAsRead && prevState[key]) {
          delete newState[key];
        }
        return newState;
      });
    });
  };
  useEffect(() => {
    if (tableRef) {
      const onClick = () => {
        readTimeController.current?.abort();
        const rowIndex = focusedCellRef.current?.rowIndex;
        if (!isNullish(rowIndex)) {
          setReadItems(prevState => ({
            ...prevState,
            [getItemKey(rowIndex)]: true
          }));
        }
      };
      const onContextMenu = _event => {
        readTimeController.current?.abort();
      };
      const onKeyDown = event => {
        const {
          actions
        } = getActionsByKeyEvent(event);
        if (actions.includes('openContextMenu')) {
          readTimeController.current?.abort();
          readItemsSnapshotRef.current = readItems;
        }
        if (event.key === 'Tab') {
          readTimeController.current?.abort();
        }
      };
      const currentTableRef = tableRef.current;
      currentTableRef?.addEventListener('click', onClick);
      currentTableRef?.addEventListener('contextmenu', onContextMenu);
      currentTableRef?.addEventListener('keydown', onKeyDown);
      return () => {
        currentTableRef?.removeEventListener('click', onClick);
        currentTableRef?.removeEventListener('contextmenu', onContextMenu);
        currentTableRef?.removeEventListener('keydown', onKeyDown);
      };
    }
  }, [getActionsByKeyEvent, getItemKey, readItems, setReadItems, tableRef]);
  return {
    readItems,
    setReadItems,
    onFocusChange,
    onContextMenuDismiss,
    onContextMenuItemSelected,
    getContextMenuItems
  };
};
//# sourceMappingURL=useReadItems.js.map